// TREE (P1, pivot spec §11): the architecture tree as tier lanes. Node states
// (researched/in-progress/available/world-gated/prereq-locked), grade badges,
// credit ghosts, obsolescence riders, and the two ways research gets staffed:
// the garage's single focus (founder-months) or company-phase steppers that
// draw engineer-months ahead of projects, split by each node's discipline mix.

import { useLayoutEffect, useRef, useState } from "react";
import type { AttrVector, TechNodeDef, TechTreeDef } from "../../engine/types.js";
import { ATTR_KEYS, DISCIPLINES } from "../../engine/types.js";
import { interpolate } from "../../engine/calendar.js";
import { archFitFactor, productAttrs } from "../../engine/systems/market.js";
import { effectiveEmCost, frontierAttrs, nodeAttrsAt } from "../../engine/systems/tree.js";
import { activeTreeOf, viewCtx, type ScreenProps } from "./shared.js";
import { money, monthLabel } from "../format.js";
import { Bar, Stepper } from "../components/widgets.js";
import { riskLine, THESIS_COPY, ThesisNotes } from "../components/ThesisNotes.js";

const ATTR_LABEL: Record<keyof AttrVector, string> = {
  perf3d: "3D",
  perf2d: "2D",
  features: "FEAT",
  compat: "COMPAT",
  efficiency: "EFF",
};

const TIER_TITLE = ["TIER 0 — FOUNDING THESES", "TIER 1 — GEN-1 EXECUTION", "TIER 2 — GEN-2", "TIER 3 — GEN-3", "TIER 4 — GEN-4"];

interface TreeEdge {
  from: string;
  to: string;
  kind: "prereq" | "credits";
}

/** Directed prereq/creditsTo edges plus exclusiveGroup clusters — all static
 *  per tree def, not view-dependent (only geometry moves with view state). */
function buildTreeEdges(tree: TechTreeDef): { edges: TreeEdge[]; groups: string[][] } {
  const edges: TreeEdge[] = [];
  const groupMap = new Map<string, string[]>();
  for (const n of tree.nodes) {
    for (const p of n.prereqs) edges.push({ from: p, to: n.id, kind: "prereq" });
    for (const c of n.creditsTo ?? []) edges.push({ from: n.id, to: c.id, kind: "credits" });
    if (n.exclusiveGroup) groupMap.set(n.exclusiveGroup, [...(groupMap.get(n.exclusiveGroup) ?? []), n.id]);
  }
  return { edges, groups: [...groupMap.values()].filter((ids) => ids.length > 1) };
}

interface EdgeLine extends TreeEdge {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
interface GroupHub {
  hub: { x: number; y: number };
  spokes: { x: number; y: number }[];
}

/** Measures card positions inside the scrollable `.lanes` container and
 *  turns edges/groups into SVG coordinates. The overlay SVG lives INSIDE
 *  `.lanes` (not viewport-fixed), so it scrolls with the content for free —
 *  positions are computed in content space (viewport delta + scrollLeft/Top),
 *  not viewport space. Recomputes on any card-affecting state change (not
 *  just window resize, since toggled focus/staffing state resizes cards
 *  without firing a native resize event). */
function useTreeEdgeGeometry(
  lanesRef: React.RefObject<HTMLDivElement | null>,
  cardRefs: React.MutableRefObject<Map<string, HTMLDivElement>>,
  edges: TreeEdge[],
  groups: string[][],
  deps: unknown[],
): { lines: EdgeLine[]; hubs: GroupHub[]; width: number; height: number } {
  const [geo, setGeo] = useState<{ lines: EdgeLine[]; hubs: GroupHub[]; width: number; height: number }>({
    lines: [],
    hubs: [],
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    const container = lanesRef.current;
    if (!container) return;

    const compute = () => {
      const containerRect = container.getBoundingClientRect();
      const posOf = (id: string): { left: number; right: number; midY: number } | null => {
        const el = cardRefs.current.get(id);
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return {
          left: r.left - containerRect.left + container.scrollLeft,
          right: r.right - containerRect.left + container.scrollLeft,
          midY: r.top - containerRect.top + container.scrollTop + r.height / 2,
        };
      };
      const lines: EdgeLine[] = [];
      for (const e of edges) {
        const a = posOf(e.from);
        const b = posOf(e.to);
        if (!a || !b) continue;
        const forward = b.left >= a.right;
        lines.push({ ...e, x1: forward ? a.right : a.left, y1: a.midY, x2: forward ? b.left : b.right, y2: b.midY });
      }
      const hubs: GroupHub[] = [];
      for (const ids of groups) {
        const pts = ids.map(posOf).filter((p): p is { left: number; right: number; midY: number } => p !== null);
        if (pts.length < 2) continue;
        hubs.push({
          hub: { x: pts.reduce((a, p) => a + p.left, 0) / pts.length, y: pts.reduce((a, p) => a + p.midY, 0) / pts.length },
          spokes: pts.map((p) => ({ x: p.left, y: p.midY })),
        });
      }
      setGeo({ lines, hubs, width: container.scrollWidth, height: container.scrollHeight });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return geo;
}

export function Tree({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const tree = activeTreeOf(content);
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  const lanesRef = useRef<HTMLDivElement>(null);
  const { edges, groups } = tree ? buildTreeEdges(tree) : { edges: [], groups: [] };
  const geo = useTreeEdgeGeometry(lanesRef, cardRefs, edges, groups, [tree, view.tree, pending]);
  const registerCard = (id: string, el: HTMLDivElement | null) => {
    if (el) cardRefs.current.set(id, el);
    else cardRefs.current.delete(id);
  };
  const m = view.monthIndex;
  if (!tree) {
    return (
      <div>
        <h2>TREE</h2>
        <span className="hint">this scenario plays without an architecture tree</span>
      </div>
    );
  }
  const ctx = viewCtx(view, content);
  const frontier = frontierAttrs(ctx, tree, m);
  const discount = content.economy.models.frontierDiscount;
  const tiers = [...new Set(tree.nodes.map((n) => n.tier))].sort((a, b) => a - b);

  // YOUR position: every node you hold, grade-scaled, aged by the world —
  // the ceiling a chip started today could snapshot (shipped parts keep the
  // nodes and grades they launched with)
  const held = Object.entries(view.tree.researched).map(([id, r]) => ({ id, grade: r.grade }));
  const mine = productAttrs(ctx, { archNodes: held }, m);
  const segments = content.markets.filter((mk) => mk.attrWeights);

  return (
    <div>
      <h2>{tree.displayName.toUpperCase()} — THE TREE</h2>
      <div className="panel">
        <div className="title">WHERE YOUR TECH STANDS — your researched stack vs. the frontier (what products are scored against)</div>
        <div className="row">
          {ATTR_KEYS.map((k) => {
            const ideal = frontier[k] * discount;
            const yours = mine[k];
            const open = ideal <= 1e-9; // the world has nothing shipping on this dimension yet
            const ratio = open ? null : yours / ideal;
            return (
              <div className="tile" key={k}>
                <div className="k">{ATTR_LABEL[k]}</div>
                <div
                  className="v"
                  style={
                    open
                      ? yours > 0
                        ? { color: "var(--cyan)" }
                        : { color: "var(--ink-faint)" }
                      : ratio! < 0.85
                        ? { color: "var(--red)" }
                        : ratio! > 1.05
                          ? { color: "var(--cyan)" }
                          : undefined
                  }
                >
                  {open ? (yours > 0 ? "LEAD" : "—") : `×${ratio!.toFixed(2)}`}
                </div>
                <div className="sub">
                  you {yours.toFixed(1)} · world {open ? "nothing yet" : ideal.toFixed(1)}
                </div>
              </div>
            );
          })}
        </div>
        {held.length > 0 && segments.length > 0 && (
          <div className="warn-text" style={{ marginTop: 6 }}>
            a chip started TODAY on your full stack:{" "}
            {segments
              .map((mk) => `${mk.displayName.split("—").pop()!.trim()} ×${archFitFactor(ctx, mk, { archNodes: held }, m).toFixed(2)}`)
              .join(" · ")}
          </div>
        )}
        {held.length === 0 && <div className="hint" style={{ marginTop: 6 }}>you hold nothing yet — the lanes below are the menu</div>}
        <span className="hint">
          yours = every node you hold, grade-scaled (A ×{content.economy.tree.gradeAttrMult.A} / C ×{content.economy.tree.gradeAttrMult.C}),
          aged by the world · the frontier adopts a node ~{content.economy.tree.frontierLagMonths}mo after it exists — shipped chips are
          snapshots and age against it
        </span>
      </div>
      <div className="lanes" ref={lanesRef}>
        <svg width={geo.width} height={geo.height} style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
          <defs>
            <marker id="tree-arrow-ink" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0,0 L8,4 L0,8 z" fill="var(--ink-dim)" />
            </marker>
            <marker id="tree-arrow-cyan" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0,0 L8,4 L0,8 z" fill="var(--cyan)" />
            </marker>
          </defs>
          {geo.lines.map((l, i) => (
            <line
              key={i}
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              stroke={l.kind === "credits" ? "var(--cyan)" : "var(--ink-dim)"}
              strokeWidth={1}
              strokeOpacity={l.kind === "credits" ? 0.7 : 0.4}
              markerEnd={l.kind === "credits" ? "url(#tree-arrow-cyan)" : "url(#tree-arrow-ink)"}
            />
          ))}
          {geo.hubs.map((h, i) => (
            <g key={i}>
              {h.spokes.map((s, j) => (
                <line
                  key={j}
                  x1={h.hub.x}
                  y1={h.hub.y}
                  x2={s.x}
                  y2={s.y}
                  stroke="var(--amber-dim)"
                  strokeWidth={1}
                  strokeDasharray="3,3"
                  strokeOpacity={0.55}
                />
              ))}
            </g>
          ))}
        </svg>
        {tiers.map((t) => (
          <div className="lane" key={t} style={{ minWidth: 280, flex: "0 0 280px" }}>
            <div className="lane-title">{TIER_TITLE[t] ?? `TIER ${t}`}</div>
            {tree.nodes
              .filter((n) => n.tier === t)
              .sort((a, b) => a.availableMonth - b.availableMonth || a.id.localeCompare(b.id))
              .map((n) => (
                <NodeCard key={n.id} node={n} registerCard={registerCard} snap={snap} content={content} session={session} />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function NodeCard({
  node: n,
  registerCard,
  snap,
  content,
  session,
}: ScreenProps & { node: TechNodeDef; registerCard: (id: string, el: HTMLDivElement | null) => void }) {
  const { view, pending } = snap;
  const m = view.monthIndex;
  const ctx = viewCtx(view, content);
  const tree = activeTreeOf(content)!;
  const inGarage = view.stage === "garage";

  const done = view.tree.researched[n.id];
  const banked = view.tree.inProgress[n.id] ?? 0;
  const credits = view.tree.credits[n.id] ?? 0;
  // provenance for banked salvage: which researched node credited this one
  const creditSources =
    credits > 0
      ? tree.nodes.filter((src) => src.id in view.tree.researched && (src.creditsTo ?? []).some((c) => c.id === n.id)).map((src) => src.displayName)
      : [];
  const worldGated = n.availableMonth > m;
  const prereqsOk = n.prereqs.every((p) => p in view.tree.researched);
  const cost = effectiveEmCost(ctx, n);
  const siblingHeld = n.exclusiveGroup
    ? tree.nodes.some((x) => x.id !== n.id && x.exclusiveGroup === n.exclusiveGroup && x.id in view.tree.researched)
    : false;
  const obsolete = n.obsolescence && m >= n.obsolescence.month;

  // live attrs (grade-scaled when researched, base preview otherwise)
  const gm = done ? content.economy.tree.gradeAttrMult[done.grade] : 1;
  const attrs = nodeAttrsAt(n, m);
  const attrText = ATTR_KEYS.filter((k) => (attrs[k] ?? 0) !== 0)
    .map((k) => `${ATTR_LABEL[k]} ${((attrs[k] ?? 0) * gm).toFixed(1)}`)
    .join(" · ");

  // garage focus / company staffing controls
  const queuedAlloc = pending.find((c) => c.type === "setGarageAlloc");
  const focusId =
    queuedAlloc && queuedAlloc.type === "setGarageAlloc" && queuedAlloc.researchNodeId !== undefined
      ? queuedAlloc.researchNodeId
      : (view.garage?.researchNodeId ?? null);
  const isFocus = focusId === n.id;
  const queuedStaff = pending.find((c) => c.type === "setTreeStaff" && c.nodeId === n.id);
  const staff = view.tree.staffing[n.id] ?? 0;
  const cashCost = n.cashCost ? Math.round(interpolate(n.cashCost, m)) : 0;

  const state = done
    ? obsolete
      ? "obsolete"
      : "researched"
    : worldGated
      ? "future"
      : !prereqsOk
        ? "locked"
        : banked > 0
          ? "in-progress"
          : "available";

  return (
    <div ref={(el) => registerCard(n.id, el)} className={`card${state === "future" || state === "locked" ? " ghost-dim" : ""}`}>
      <div className="head">
        <span className="id" style={done ? (obsolete ? { color: "var(--red)" } : {}) : { color: "var(--ink)" }}>
          {n.displayName}
        </span>
        {done && (
          <span className={`badge ${done.grade === "A" ? "cyan" : done.grade === "B" ? "" : "amber"}`} title={`researched ${monthLabel(done.month)}`}>
            {done.grade}
          </span>
        )}
      </div>
      <div className="meta">
        {attrText || "no attrs — capability, not a spec line"}
        {n.exclusiveGroup && (
          <>
            {" "}
            · <span className={siblingHeld && !done ? "warn-text" : ""}>{n.exclusiveGroup} bet</span>
          </>
        )}
      </div>

      {/* the dossier speaks in risks, not schedules — the content files know
          the obsolescence dates; the player only gets to suspect them */}
      {state !== "obsolete" &&
        (THESIS_COPY[n.id] ? (
          <ThesisNotes node={n} view={view} content={content} showWindow={false} />
        ) : (
          riskLine(n) && (
            <div className="warn-text" style={{ fontSize: 11, marginBottom: 4 }}>
              {riskLine(n)}
            </div>
          )
        ))}
      {state === "obsolete" && (
        <div className="bad-text" title={n.obsolescence!.body}>
          {n.obsolescence!.headline} — attrs ×{n.obsolescence!.factor}
        </div>
      )}
      {state === "future" && <div className="hint">world gate: {monthLabel(n.availableMonth)}</div>}
      {state === "locked" && (
        <div className="hint">
          needs {n.prereqs.filter((p) => !(p in view.tree.researched)).map((p) => tree.nodes.find((x) => x.id === p)?.displayName ?? p).join(", ")}
        </div>
      )}

      {(state === "available" || state === "in-progress") && (
        <>
          <Bar frac={banked / cost} label="research" right={`${banked.toFixed(1)} / ${cost.toFixed(0)} EM`} />
          {credits > 0 && (
            <div className="hint" style={{ color: "var(--cyan)" }}>
              {credits.toFixed(1)} EM salvaged
              {creditSources.length > 0 ? ` from ${creditSources.join(" + ")}` : ""} — that work carries over here
            </div>
          )}
          {inGarage && n.garageResearchable && n.pitchAppeal && (
            <div className="hint">
              {n.exclusiveGroup === "thesis"
                ? `pitch ${interpolate(n.pitchAppeal, m).toFixed(2)} — the story itself`
                : `pitch +${interpolate(n.pitchAppeal, m).toFixed(2)} once a thesis is held`}
            </div>
          )}
          {siblingHeld && (
            <div className="warn-text" style={{ fontSize: 11 }}>
              organizational unlearning: ×{content.economy.tree.exclusivePenaltyMult} while a rival {n.exclusiveGroup} bet is held
            </div>
          )}
          {cashCost > 0 && <div className="hint">+{money(cashCost)} capital purchase on completion{n.tapeoutCoverageBoost ? " · boosts every tape-out's coverage" : ""}</div>}
          {inGarage ? (
            n.garageResearchable ? (
              <div className="actions">
                <button
                  className={`act${isFocus ? " queued" : ""}`}
                  onClick={() => {
                    const alloc =
                      queuedAlloc && queuedAlloc.type === "setGarageAlloc" ? queuedAlloc.alloc : (view.garage?.alloc ?? {});
                    session.queue({ type: "setGarageAlloc", alloc: { ...alloc }, researchNodeId: isFocus ? null : n.id });
                  }}
                >
                  {isFocus ? "★ GARAGE FOCUS — click to clear" : "SET AS GARAGE FOCUS"}
                </button>
              </div>
            ) : (
              <div className="hint">needs a real company behind it</div>
            )
          ) : (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className="k" style={{ fontSize: 10 }}>ENGINEERS</span>
                <Stepper
                  value={staff}
                  queued={queuedStaff && queuedStaff.type === "setTreeStaff" ? queuedStaff.engineers : undefined}
                  bigStep={2}
                  onChange={(v) => session.queue({ type: "setTreeStaff", nodeId: n.id, engineers: v })}
                />
              </div>
              <div className="hint">
                draws {DISCIPLINES.filter((d) => (n.disciplineMix[d] ?? 0) > 0)
                  .map((d) => `${(n.disciplineMix[d] ?? 0).toFixed(2)}${d[0]!.toUpperCase()}`)
                  .join(" / ")} per head, ahead of projects
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
